import { z } from 'zod';

// Every key is always sent; a value the flow does not have comes as null.

export const ApprovalNodeStatusSchema = z.enum(['draft', 'pending', 'approved', 'rejected']);
export type ApprovalNodeStatus = z.infer<typeof ApprovalNodeStatusSchema>;

export const ApprovalLevelStatusSchema = z.enum(['draft', 'pending', 'approved', 'rejected', 'inactive']);
export type ApprovalLevelStatus = z.infer<typeof ApprovalLevelStatusSchema>;

export const ApprovalFlowStatusSchema = z.enum(['draft', 'active']);
export type ApprovalFlowStatus = z.infer<typeof ApprovalFlowStatusSchema>;

// `note` is nullish, not nullable: a backend without approval notes does not send the key.
export const ApprovalNodeSchema = z.object({
  id: z.string().nullable(),
  username: z.string().nullable(),
  userid: z.string().nullable(),
  status: ApprovalNodeStatusSchema,
  updated: z.string().nullable(),
  note: z.string().nullish(),
});
export type ApprovalNode = z.infer<typeof ApprovalNodeSchema>;

export const ApprovalLevelNodeSchema = z.object({
  username: z.string().nullable(),
  userid: z.string().nullable(),
  status: ApprovalNodeStatusSchema.nullable(),
  updated: z.string().nullable(),
  note: z.string().nullish(),
});
export type ApprovalLevelNode = z.infer<typeof ApprovalLevelNodeSchema>;

export const ApprovalLevelSchema = z.object({
  id: z.string().nullable(),
  next: z.string().nullable(),
  nodes: z.array(ApprovalLevelNodeSchema).nullable(),
  min_amount: z.number().nullable(),
  min_approvers: z.number().nullable(),
  flow_type: z.string().nullable(),
  status: ApprovalLevelStatusSchema.nullable(),
});
export type ApprovalLevel = z.infer<typeof ApprovalLevelSchema>;

export const ApprovalFlowSchema = z.object({
  creator: z.string().nullable(),
  updated: z.string().nullable(),
  editable: z.boolean(),
  group_id: z.string(),
  advanced_group_id: z.string(),
  status: ApprovalFlowStatusSchema.nullable(),
  next: z.union([z.string(), z.array(z.string())]).nullable(),
  next_level: z.string().nullable(),
  levels: z.array(ApprovalLevelSchema).nullable(),
  nodes: z.array(ApprovalNodeSchema).nullable(),
  rejectReason: z.string().nullable(),
  rejectMessage: z.string().nullable(),
  rejectReasons: z.record(z.string(), z.string()),
  signature: z.string().nullable(),
  error: z.string().nullable(),
});
export type ApprovalFlow = z.infer<typeof ApprovalFlowSchema>;

export const DocumentApproveBodySchema = z.object({
  documentID: z.string(),
  note: z.string().max(1000).optional(),
});

export const DocumentRejectBodySchema = z.object({
  documentID: z.string(),
  reasonID: z.string(),
  message: z.string().optional(),
});
